import React from "react";
import { Home, Building2, Crown, Mountain, ChevronDown } from "lucide-react";
import { Card, Input, Select, PlaceSearch } from "./FormComponents";

const propertyTypes = [
  {
    id: "Apartment",
    title: "Apartment",
    description: "A furnished, self-contained unit suitable for both short and extended stays.",
    icon: Building2
  },
  {
    id: "Vacation Home",
    title: "Vacation Home",
    description: "A standalone home with its own private entrance, intended for vacation stays.",
    icon: Home
  },
  {
    id: "Villa",
    title: "Villa",
    description: "A private standalone home that offers more space, privacy, and a premium feel.",
    icon: Crown
  },
  {
    id: "Chalet",
    title: "Chalet",
    description: "A standalone vacation home, typically identified by its sloping roof design.",
    icon: Mountain
  }
];

export const Step1 = ({ data, updateData, isLight, showValidation }) => {
  const handleChange = (field, value) => {
    updateData("propertyProfile", { ...data, [field]: value });
  };

  const showTypeError = showValidation && !data.type;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <label className={`text-[10px] uppercase tracking-[0.18em] font-bold mb-1.5 block ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
          What best describes your property? <span className="text-red-500">*</span>
        </label>
        {showTypeError && (
          <p className="text-red-500 text-xs font-medium -mt-1 animate-pulse">Please select a property type to continue.</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {propertyTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = data.type === type.id;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => handleChange("type", type.id)}
                className={`flex flex-col p-6 rounded-3xl border transition-all text-left h-full gap-5 ${
                  isSelected 
                    ? isLight 
                      ? "bg-neutral-900 border-neutral-900 text-white shadow-[0_12px_30px_rgba(15,23,42,0.55)]"
                      : "bg-white/15 border-white/10 text-neutral-50 shadow-[0_12px_30px_rgba(0,0,0,0.8)]" 
                    : isLight
                      ? "bg-white border-neutral-200 hover:border-neutral-300 text-neutral-600 hover:bg-neutral-50 shadow-sm"
                      : "bg-white/5 border-white/5 hover:border-white/15 text-neutral-400 hover:bg-white/10"
                }`}
              >
                <div className={`h-10 w-10 rounded-2xl flex items-center justify-center border transition-all ${
                  isSelected 
                    ? "bg-white/20 border-white/20 text-white" 
                    : isLight ? "bg-neutral-50 border-neutral-200 text-neutral-400" : "bg-white/5 border-white/10 text-neutral-500"
                }`}>
                  <Icon size={20} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className={`text-base font-semibold tracking-tight ${
                    isSelected 
                      ? "text-white"
                      : isLight ? "text-neutral-900" : "text-neutral-100"
                  }`}>
                    {type.title}
                  </span>
                  <p className={`text-xs leading-relaxed ${
                    isSelected 
                      ? "text-white/70"
                      : isLight ? "text-neutral-500" : "text-neutral-500"
                  }`}>
                    {type.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <Card title="About Your Property" icon={Home} subtitle="Tell us the basics." isLight={isLight}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Input 
            label="Property Name" 
            required 
            placeholder="Monastiraki Apartment" 
            value={data.name}
            onChange={(e) => handleChange("name", e.target.value)}
            isLight={isLight}
            showValidation={showValidation}
          />
          <div className="md:col-span-1" />
          <div className="md:col-span-2">
            <PlaceSearch 
              label="Full Address" 
              required 
              placeholder="Ermou 32, Athens 105 63, Greece" 
              value={data.address}
              isLight={isLight}
              showValidation={showValidation}
              onSelect={(place) => {
                updateData("propertyProfile", { 
                  ...data, 
                  address: place.address,
                  lat: place.lat,
                  lon: place.lon
                });
              }}
            />
          </div>
          <Input 
            label="Floor" 
            required
            placeholder="1st, Ground, 5th" 
            value={data.floor}
            onChange={(e) => handleChange("floor", e.target.value)}
            isLight={isLight}
            showValidation={showValidation}
          />
          <Input 
            label="Apartment / Unit Number" 
            placeholder="101, A3, 6B" 
            value={data.unitNumber}
            onChange={(e) => handleChange("unitNumber", e.target.value)}
            isLight={isLight}
          />
          <div className="md:col-span-2">
            <div className="flex gap-4 items-end">
              <Input 
                label="Total Size"
                type="text"
                inputMode="decimal"
                required
                placeholder="50"
                value={data.size}
                isLight={isLight}
                showValidation={showValidation}
                min="0"
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, "");
                  // Ensure only one decimal point
                  const parts = val.split(".");
                  const filteredVal = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : val;
                  handleChange("size", filteredVal);
                }}
              />
              <Select 
                label="Unit"
                options={["m²", "ft²"]}
                className="w-32"
                value={data.sizeUnit}
                hidePlaceholder
                isLight={isLight}
                onChange={(e) => handleChange("sizeUnit", e.target.value)}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
